import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { Skeleton } from '@/components/ui/skeleton';

interface AuditEvent {
  id: string;
  event_type: string;
  resource_type: string | null;
  resource_id: string | null;
  metadata: Record<string, unknown>;
  occurred_at: string;
}

const EVENT_LABELS: Record<string, string> = {
  'vendor.login': 'Signed in',
  'vendor.invitation.accepted': 'Accepted invitation',
  'vendor.assessment.submitted': 'Submitted assessment',
  'vendor.assessment.response_saved': 'Saved response',
  'vendor.assessment.evidence_uploaded': 'Uploaded evidence',
  'vendor.certification.uploaded': 'Uploaded certification',
  'vendor.team.member_invited': 'Invited team member',
  'vendor.federation.linked': 'Linked identity provider',
};

function eventLabel(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType.replace(/^vendor\./, '').replace(/\./g, ' ');
}

export function VendorActivityFeed() {
  const { data: events, isLoading } = useQuery({
    queryKey: ['portal-activity'],
    queryFn: async () => {
      const { data } = await apiClient.get('/portal/activity?limit=10');
      return data.data as AuditEvent[];
    },
    staleTime: 60_000,
  });

  return (
    <div className="rounded-lg border border-border-subtle bg-bg-surface p-4">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-4 w-4 text-text-secondary" />
        <h3 className="text-sm font-semibold text-text-primary">Recent activity</h3>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-2">
              <Skeleton className="h-4 w-4 rounded-full mt-0.5 shrink-0" />
              <div className="space-y-1 flex-1">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && (!events || events.length === 0) && (
        <p className="text-sm text-text-secondary">No recent activity.</p>
      )}

      {!isLoading && events && events.length > 0 && (
        <ol className="space-y-3">
          {events.map((event) => (
            <li key={event.id} className="flex items-start gap-2.5">
              <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent-cyan shrink-0" />
              <div>
                <p className="text-sm text-text-primary">{eventLabel(event.event_type)}</p>
                <p className="text-xs text-text-secondary">
                  {formatDistanceToNow(parseISO(event.occurred_at), { addSuffix: true })}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
